import { useEffect, useRef } from 'react';
import useFilterStore, { getFilterStore, initialFilters } from 'zustandStore/useFilterStore';
import S3Util from 'lib/S3Util';
import CommonsUtil from 'lib/Commons';
import { isNZLocale } from 'lib/PropertyUtils';
import { FILTERS, FILTERS_NZ } from 'constants/filterOptions';
import { useSelector } from 'react-redux';
import RapidBatchSearchApi from 'api/RapidBatchSearchApi';
import { LISTINGS, SALES, RENTALS } from 'constants/comparableTypes';

const COMPARABLE_STATE = {
    [LISTINGS]: 'comparableListings',
    [SALES]: 'comparableSales',
    [RENTALS]: 'comparableRentals',
};

const FILTER_OPTIONS_ERROR = 'Sorry, there was an error fetching the filter options. Please refresh the page to try again.';

const formatOptions = (options) => {
    if (!options.length) {
        return {
            data: [],
            error: 'No filter options available',
        };
    }
    return {
        data: options.map(opt => ({ label: opt.value, value: opt.value })),
        error: null,
    };
};

const getFilterOptions = async (filePath) => {
    try {
        const response = await fetch(S3Util.getAssetDomainLegacy(filePath));
        const jsonResponse = await response.json();
        const data = CommonsUtil.sortStringArrayNumericLast(jsonResponse);
        return {
            data: data.map(opt => ({ label: opt, value: opt })),
            error: null,
        };
    } catch (error) {
        return {
            data: [],
            error: FILTER_OPTIONS_ERROR,
        };
    }
};

export const fetchFilterOptionsFromJson = async () => {
    const filters = isNZLocale() ? FILTERS_NZ : FILTERS;
    const [propertySubType, landUse, devZone] = await Promise.all([
        getFilterOptions(filters.PROPERTY_SUB_TYPE),
        getFilterOptions(filters.LAND_USE),
        getFilterOptions(filters.DEV_ZONE),
    ]);
    return { propertySubType, landUse, devZone };
};

export const handleAggregateSearch = async (params) => {
    const { setPropertySubTypeOptions, setLandUseOptions, setDevZoneOptions } = getFilterStore();
    try {
        const response = await RapidBatchSearchApi.getAggregatedSearch(params);
        const [subType, landUse, devZones] = response.data.length === 3
            ? response.data.map(aggregation => aggregation.fieldValues)
            : [[], [], []];
        setPropertySubTypeOptions(formatOptions(subType));
        setLandUseOptions(formatOptions(landUse));
        setDevZoneOptions(formatOptions(devZones));
    } catch (e) {
        const { propertySubType, landUse, devZone } = await fetchFilterOptionsFromJson();
        setPropertySubTypeOptions(propertySubType);
        setLandUseOptions(landUse);
        setDevZoneOptions(devZone);
    }
};

const useFilterOptions = (comparableType) => {
    const {
        appliedFilters,
        propertySubTypeOptions,
        landUseOptions,
        devZoneOptions,
    } = useFilterStore();
    const {
        localityName,
        postcode,
        addressState,
        latitude,
        longitude,
        propertyId,
        addressTown,
        comparableState,
        singleLineAddress,
    } = useSelector(state => ({
        localityName: state?.propertyDetail?.localityName,
        postcode: state?.propertyDetail?.addressPostcode,
        addressState: state?.propertyDetail?.addressState,
        latitude: state?.propertyDetail?.latitude,
        longitude: state?.propertyDetail?.longitude,
        propertyId: state?.propertyDetail?.propertyId,
        addressTown: state?.propertyDetailCache?.addressTown,
        singleLineAddress: state?.propertyDetailCache?.singleLineAddress,
        comparableState: state[COMPARABLE_STATE[comparableType]],
    }));

    const areOptionsPopulated = (
        propertySubTypeOptions?.loaded === true &&
        landUseOptions?.loaded === true &&
        devZoneOptions?.loaded === true
    );

    const appliedDistance = appliedFilters?.[comparableType]?.distance;
    const prevDistanceRef = useRef({});

    useEffect(() => {
        const prevDistance = prevDistanceRef.current[comparableType];
        prevDistanceRef.current[comparableType] = appliedDistance;

        const distanceChanged = prevDistance !== undefined && prevDistance !== appliedDistance;

        if (!distanceChanged && areOptionsPopulated) return;

        const { pagination: { page } = {}, sortBy } = comparableState ?? {};
        if (singleLineAddress && latitude && longitude) {
            const { date, propertyType, ...essentialForDynamicFilters } = initialFilters?.[comparableType] || {};
            handleAggregateSearch({
                addressState,
                addressTown,
                comparableType,
                filters: {
                    ...essentialForDynamicFilters,
                    distance: appliedDistance,
                },
                localityName,
                postcode,
                page,
                sortBy,
                coordinates: { lat: latitude, lon: longitude },
                targetId: propertyId,
            });
        }
    }, [singleLineAddress, comparableType, latitude, longitude, areOptionsPopulated, appliedDistance]);
};

export default useFilterOptions;